import clsx from "clsx";
import { ChangeEvent, useEffect, useMemo, useRef } from "react";
import Addicon from "@mui/icons-material/Add";
import Divider from "@mui/material/Divider";
import { useFormContext } from "react-hook-form";
import CField from "$forms/CField";
import useAuth from "$hooks/useAuth";
import { Button, Text } from "$components";
import CompanySelect from "$forms/CompanySelect";
import ContactSelect from "$forms/ContactSelect";
import { useStyles } from "../../styles/DeliveryPicker.styles";
import { nameRegexp, NAME_ERROR_MSG, normalizeName } from "$utils/utils";
import { useDeliveryEDBContext } from "$containers/EDB/ProfileEDB/context/DeliveryContext";

export default function ContactForm() {
  const classes = useStyles();
  const { isAdmin, isContributor, user } = useAuth();

  const {
    control,
    formState: { errors },
    trigger,
    setValue,
    getValues,
  } = useFormContext();

  const {
    dockyard,
    setContactForm,
    intervenantCompany,
    intervenantOptions,
    openIntervenantPopin,
    toggleNewContactDrawer,
  } = useDeliveryEDBContext();

  const companySelectOptions = useMemo(() => {
    const baseOptions = intervenantOptions || [];

    const userCompany = user?.userInfo?.userCompany;
    const fallbackCompany = userCompany || undefined;

    if (fallbackCompany) {
      return [...intervenantOptions, fallbackCompany];
    }

    return baseOptions;
  }, [intervenantOptions, user?.userInfo?.userCompany]);

  // Calculate the company ID to use for contact selection (user's company for intervenants)
  const contactCompanyId = useMemo(() => {
    const userCompanyId =
      user?.userInfo?.userCompany?._id || user?.userInfo?.userCompany?.id;
    return isContributor() && userCompanyId
      ? userCompanyId
      : intervenantCompany;
  }, [isContributor, intervenantCompany, user?.userInfo?.userCompany]);

  // Always set the user's company for intervenant users
  useEffect(() => {
    if (isContributor() && user?.userInfo?.userCompany) {
      const userCompanyId =
        user.userInfo.userCompany._id || user.userInfo.userCompany.id;
      if (userCompanyId) {
        setValue("intervenantCompany", userCompanyId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userInfo?.userCompany, setValue]);

  const lastAutoFilledContactIdRef = useRef<string | null>(null);

  return (
    <>
      <div className={classes.contentStep3}>
        <div>
          <div className={classes.contentStep3Title}>
            <Text className={classes.subtitle}>
              Entreprise intervenante et contact (facultatifs)
            </Text>
          </div>

          <div
            className={classes.contentStep3Content}
            style={{
              display: "flex",
              alignItems: "flex-start",
              width: "100%",
              marginBottom: 12,
            }}
          >
            <div
              style={{
                paddingRight: 12,
                marginRight: 12,
                borderRight: "2px solid #dddddd",
                display: "flex",
              }}
            >
              <div style={{ marginRight: 4, width: 300 }}>
                <CField
                  control={control}
                  name="intervenantCompany"
                  error={errors.intervenantCompany}
                >
                  {(props: any) => {
                    // For intervenant users, always use their company
                    const userCompanyId =
                      user?.userInfo?.userCompany?._id ||
                      user?.userInfo?.userCompany?.id;
                    const defaultValue =
                      isContributor() && userCompanyId
                        ? userCompanyId
                        : props.value || dockyard?.intervenantCompany;
                    return (
                      <CompanySelect
                        companyOptions={companySelectOptions}
                        value={defaultValue}
                        onChange={(val) => {
                          props.onChange(val);
                          setValue("intervenantContact", null);
                          setValue("entrepotName", null);
                          setValue("dispositionAdress", "");
                          setValue("dispositionAdress2", "");
                          setValue("dispositionPostalCode", "");
                          setValue("dispositionCity", "");
                          setValue("dispositionCountry", "");
                        }}
                        ignoreSideBarCompanyFilter
                        minCharsToTrigerSearch={0}
                        rowsPerPage={99999}
                        disabled={isContributor()}
                      />
                    );
                  }}
                </CField>
              </div>
              {isAdmin() && (
                <Button
                  color="secondary"
                  variant="contained"
                  onClick={openIntervenantPopin}
                  style={{ padding: 0, minWidth: 40 }}
                >
                  <Addicon />
                </Button>
              )}
            </div>

            <div
              style={{
                display: "flex",
              }}
            >
              <div style={{ width: 300, marginRight: 4 }}>
                <CField
                  control={control}
                  name="intervenantContact"
                  error={errors.intervenantContact}
                >
                  {(props: any) => (
                    <ContactSelect
                      companyId={contactCompanyId}
                      value={props.value}
                      onChange={(contact, fullContact) => {
                        setContactForm(fullContact);
                      }}
                      onDataFetched={(contacts) => {
                        // When editing / duplicating an EDB, the contact can already be selected.
                        // In this case, the onChange handler is not triggered, so we must
                        // populate the recovery contact fields once options are loaded.
                        if (!props.value) return;
                        const selectedId = String(props.value);
                        if (lastAutoFilledContactIdRef.current === selectedId)
                          return;

                        const needsAutofill =
                          !getValues("firstnameContactRecovery") ||
                          !getValues("lastnameContactRecovery") ||
                          !getValues("emailContactRecovery") ||
                          !getValues("phoneContactRecovery");
                        if (!needsAutofill) {
                          lastAutoFilledContactIdRef.current = selectedId;
                          return;
                        }

                        const found = contacts?.find(
                          (c: any) => String(c?._id ?? c?.id) === selectedId
                        );
                        if (found) {
                          lastAutoFilledContactIdRef.current = selectedId;
                          setContactForm(found);
                        }
                      }}
                      disabled={!contactCompanyId}
                      unstyled
                    />
                  )}
                </CField>
              </div>
              {isAdmin() && (
                <Button
                  color="secondary"
                  variant="contained"
                  onClick={() => toggleNewContactDrawer(true)}
                  style={{ padding: 0, minWidth: 40 }}
                  disabled={!intervenantCompany}
                >
                  <Addicon />
                </Button>
              )}
            </div>
          </div>
          <Divider className={classes.divider} />
        </div>
      </div>
      <div className={`${classes.contentStep3} ${"show"}`}>
        <div className={classes.contentStep3Title}>
          <Text className={classes.subtitle}>
            Contact de récupération du matériel
          </Text>
        </div>
        <div
          className={clsx(
            classes.contentStep3Content,
            classes.flex,
            classes.gap
          )}
        >
          <CField
            control={control}
            name="lastnameContactRecovery"
            error={errors.lastnameContactRecovery}
            label="Nom"
            required
            rules={{
              pattern: {
                value: nameRegexp,
                message: NAME_ERROR_MSG,
              },
            }}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setValue(
                "lastnameContactRecovery",
                normalizeName(e.target.value)
              );
              if (e.target.value) {
                trigger("lastnameContactRecovery");
              }
            }}
          />
          <CField
            control={control}
            name="firstnameContactRecovery"
            error={errors.firstnameContactRecovery}
            label="Prénom"
            required
          />
          <CField
            control={control}
            name="emailContactRecovery"
            error={errors.emailContactRecovery}
            label="Email"
            required
            rules={{
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: "Veuillez saisir une adresse e-mail valide",
              },
            }}
          />
          <CField
            control={control}
            name="phoneContactRecovery"
            error={errors.phoneContactRecovery}
            label="Téléphone"
            required
            rules={{
              pattern: {
                value: /^([0-9]){1,10}$/i,
                message: "Veuillez saisir un numéro de téléphone valide",
              },
            }}
          />
        </div>
        <Divider className={classes.divider} />
      </div>
    </>
  );
}
